import React, { useCallback, useEffect, useRef, useState } from "react";
import { aiApi } from "../../api/endpoints";
import { FactChip } from "../chips/FactChip";
import { SeverityChip } from "../chips/SeverityChip";
import { AIMandatedLabel } from "./AIMandatedLabel";

/**
 * ExplainFindingDialog — AI interpretation surface for a single finding.
 *
 * Contract:
 *   • The deterministic finding is NEVER modified — this dialog only
 *     surfaces LLM interpretation prose. Numbers/severity/status remain
 *     the engine's word.
 *   • Every response is stamped with model + prompt_version so a
 *     reviewer opening a historical run knows the provenance of the AI
 *     content.
 *   • On guardrail rejection the backend returns SAFE_FALLBACK; we
 *     render it with a visible "AI response was rejected" banner.
 *   • Enter/Space on the trigger open the dialog. Escape closes and
 *     restores focus. Tab is trapped inside the panel.
 *
 * Accessibility mirrors ProvenancePopover — same in-house model, no
 * external primitive dependency.
 */
export function ExplainFindingDialog({ workspaceId, runId, finding }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const previousFocusRef = useRef(null);

  const runExplain = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await aiApi.explainFinding(workspaceId, runId, finding.id);
      setPayload(res);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, runId, finding.id]);

  // Open the panel and fire the request. We ALWAYS refetch on open
  // (including immediate reopens) so `payload.cached` reflects the
  // backend's current answer. The backend response cache guarantees this
  // is cheap — the LLM is not re-invoked on a cache hit.
  const openPanel = useCallback(() => {
    previousFocusRef.current = document.activeElement;
    setOpen(true);
    runExplain();
  }, [runExplain]);

  const closePanel = useCallback(() => {
    setOpen(false);
    // Restore focus to trigger.
    const prev = previousFocusRef.current;
    if (prev && typeof prev.focus === "function") {
      prev.focus();
    } else if (triggerRef.current) {
      triggerRef.current.focus();
    }
  }, []);

  // Keyboard handling on the trigger — Enter and Space open the dialog.
  const onTriggerKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openPanel();
      }
    },
    [openPanel],
  );

  // Escape closes; Tab wraps focus within the panel.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closePanel();
        return;
      }
      if (e.key === "Tab" && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll(
          'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, closePanel]);

  // Move focus into the panel on open.
  useEffect(() => {
    if (open && closeRef.current) {
      // Slight delay to allow the panel to render before focusing.
      const id = window.setTimeout(() => closeRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const rejected = payload?.rejected === true;
  const cached = payload?.cached === true;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openPanel}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid={`explain-finding-trigger-${finding.id}`}
        className="inline-flex items-center gap-1 rounded border border-violet-300 bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-800 hover:bg-violet-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
      >
        <span aria-hidden="true" className="text-violet-600">✦</span>
        Explain this finding
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`AI explanation for finding ${finding.rule_id}`}
          data-testid={`explain-finding-dialog-${finding.id}`}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onMouseDown={(e) => {
            // Close on backdrop click. Panel body has its own mousedown
            // handler that stopPropagations so clicks inside don't close.
            if (e.target === e.currentTarget) closePanel();
          }}
        >
          <div
            ref={panelRef}
            onMouseDown={(e) => e.stopPropagation()}
            className="w-full max-w-2xl overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
          >
            <header className="flex items-start justify-between gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3">
              <div className="min-w-0">
                <AIMandatedLabel
                  testId={`explain-mandated-label-${finding.id}`}
                />
                <h2 className="mt-0.5 truncate text-sm font-medium text-slate-900">
                  {finding.rule_id} · v{finding.rule_version}
                </h2>
                <div className="mt-1 flex items-center gap-2">
                  <SeverityChip severity={finding.effective_severity} />
                  <FactChip source="INFERRED" />
                  {cached && (
                    <span
                      data-testid={`explain-cached-${finding.id}`}
                      className="text-[10px] uppercase tracking-wider text-slate-500"
                    >
                      cached
                    </span>
                  )}
                </div>
              </div>
              <button
                ref={closeRef}
                type="button"
                onClick={closePanel}
                data-testid={`explain-finding-close-${finding.id}`}
                className="rounded p-1 text-slate-500 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                aria-label="Close AI explanation"
              >
                <CloseIcon />
              </button>
            </header>

            <div className="max-h-[70vh] overflow-y-auto px-4 py-4">
              {loading && (
                <div
                  data-testid={`explain-loading-${finding.id}`}
                  className="text-sm text-slate-500"
                >
                  Consulting the AI reviewer…
                </div>
              )}
              {error && (
                <div
                  data-testid={`explain-error-${finding.id}`}
                  className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-800"
                >
                  {error?.body?.error?.code === "LLM_NOT_CONFIGURED" &&
                    "AI is not configured on this deployment."}
                  {error?.body?.error?.code === "LLM_DAILY_CAP_EXCEEDED" &&
                    "The daily AI spend cap for this workspace has been reached."}
                  {error?.body?.error?.code === "LLM_PROVIDER_UNAVAILABLE" &&
                    "The AI provider is temporarily unavailable. Try again later."}
                  {!["LLM_NOT_CONFIGURED", "LLM_DAILY_CAP_EXCEEDED", "LLM_PROVIDER_UNAVAILABLE"].includes(
                    error?.body?.error?.code,
                  ) && (error?.body?.error?.message || "Something went wrong.")}
                </div>
              )}
              {payload && !loading && !error && (
                <ExplainBody payload={payload} finding={finding} />
              )}
            </div>

            <footer className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-[10px] text-slate-500">
              Model: <code>{payload?.model || "—"}</code>
              {" · "}
              Prompt: <code>v{payload?.prompt_version || "—"}</code>
              {payload?.created_at && (
                <>
                  {" · "}
                  Generated <time dateTime={payload.created_at}>{payload.created_at}</time>
                </>
              )}
            </footer>
          </div>
        </div>
      )}
    </>
  );
}

function ExplainBody({ payload, finding }) {
  const response = payload.response || {};
  return (
    <div className="space-y-4" data-testid={`explain-body-${finding.id}`}>
      {payload.rejected && (
        <div
          data-testid={`explain-rejected-banner-${finding.id}`}
          className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
        >
          <div className="font-medium">The AI response was rejected by the guardrail.</div>
          <div className="mt-1">
            Reason: <code>{payload.reason_code}</code>. The deterministic
            finding above is unchanged. The safe fallback message is shown
            below.
          </div>
        </div>
      )}
      <Section title="Summary">{response.summary}</Section>
      <Section title="Why it matters">{response.why_it_matters}</Section>
      <Section title="Interpretation">{response.interpretation}</Section>

      {response.facts_referenced?.length > 0 && (
        <div>
          <SectionTitle>Facts referenced</SectionTitle>
          <ul className="mt-1 space-y-1 text-xs">
            {response.facts_referenced.map((f, i) => (
              <li
                key={i}
                data-testid={`explain-fact-ref-${finding.id}-${i}`}
                className="flex items-center gap-2 rounded bg-slate-50 px-2 py-1"
              >
                <FactChip source="CALCULATED" />
                <code className="font-mono text-slate-700">{f.fact_id}</code>
                <span className="text-slate-500">·</span>
                <span className="text-slate-800">{f.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {response.suggested_next_steps?.length > 0 && (
        <div>
          <SectionTitle>Suggested next steps</SectionTitle>
          <ul className="mt-1 list-disc pl-5 text-sm text-slate-800 space-y-1">
            {response.suggested_next_steps.map((s, i) => (
              <li key={i} data-testid={`explain-next-step-${finding.id}-${i}`}>
                {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {response.validation_recommendations?.length > 0 && (
        <div>
          <SectionTitle>Validation recommendations</SectionTitle>
          <ul className="mt-1 list-disc pl-5 text-sm text-slate-800 space-y-1">
            {response.validation_recommendations.map((s, i) => (
              <li key={i} data-testid={`explain-validation-${finding.id}-${i}`}>
                {s}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  if (!children) return null;
  return (
    <div>
      <SectionTitle>{title}</SectionTitle>
      <p className="mt-1 text-sm text-slate-800">{children}</p>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-widest text-slate-500">
      {children}
    </div>
  );
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12" />
      <path d="M6 18L18 6" />
    </svg>
  );
}
